/**
 * The operator's name for the phase a workflow run stopped in.
 *
 * Consumers: `formatWorkflowErrorMessage`, which reads it into the one sentence a
 * contentless refusal can offer, and the parameter sidebar's failure banner, which
 * printed the raw `validatingData` before this map (SYD-2445).
 *
 * Each phrase reads after "at the" and before "stage". `recipient lookup` takes the
 * word the send action editor and the parameter sidebar already use, `Recipients`;
 * `action run` takes the editor's `Actions` list; the request check and the send
 * itself have no name on screen, so they get plain English.
 *
 * The argument is a plain string, not `WorkflowRunMarker['stage']`: it arrives off
 * an untrusted wire through `CallableErrorDetails`. An unmapped stage is returned
 * unchanged, which still reads as a noun in the sentence around it.
 */

import type { WorkflowRunMarker } from '#types/System.CallableError.types';

/** Every stage `executeWorkflow` can mark, so a new one cannot ship unnamed. */
const LABEL_BY_STAGE: Record<WorkflowRunMarker['stage'], string> = {
  validatingData: 'data check',
  resolvingTargets: 'recipient lookup',
  actionLoop: 'action run',
  send: 'sending',
};

/** The same map read by an unvalidated key, without asserting the union. */
const labelByUnvalidatedStage: Record<string, string> = LABEL_BY_STAGE;

export const workflowRunStageLabel = (stage: string): string =>
  labelByUnvalidatedStage[stage] ?? stage;
